/**
 * @file RedeliverOrderModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description "Refused — redeliver…" — record a delivery the customer refused
 *              on its day, where the goods are held, and when they go again
 *              (Cam, 15 Oct 2026; SO-D10289). Also "Edit redelivery…" and
 *              Cancel redelivery, and the record of earlier ones. The rules are
 *              utils/redelivery.js (the server's copy is generated from it);
 *              the server is services/orderRedeliveryService.js.
 *
 *              The order and its PO-D stay exactly as they are — nothing is
 *              bought again and the supplied quantity stays. On the
 *              redelivery date each ticked line is a row on the planning
 *              board, collected from where it is held; its haulier, driver
 *              and vehicle are set there.
 *
 *              Used from Search Customer Orders, the order window, both
 *              planning boards (hooks/useBoardRedeliver), Search Purchase
 *              Orders and the PO window (PoHubActions) — and from the
 *              Redelivery pill (RedeliveryBadge).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Textarea from './Textarea';
import DateInput from './DateInput';
import SearchableDropdown from './SearchableDropdown';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { formatQuantity } from '../utils/numberFormatters';
import { ukDay } from '../utils/transportLock';
import {
  REDELIVERY_STOCK_VALUE, REDELIVERY_OUT_OF_SCOPE, heldAtLabel, effectiveRedeliveryStatus,
  redeliverySaveProblem, redeliveryDateProblem, ukShortDay
} from '../utils/redelivery';
import './Redelivery.css';

/** The "where it is held" options: STOCK (our own site) pinned first, then every active contact. */
export function heldAtOptions(contacts) {
  return [
    { value: REDELIVERY_STOCK_VALUE, badge: 'STOCK', label: 'Excel Agri Ltd. — our own site', sticky: true,
      searchText: 'stock our site excel agri' },
    ...(contacts || []).map(c => ({
      value: String(c.id),
      badge: c.account_no || ' ',
      label: c.company_name,
      searchText: `${c.account_no || ''} ${c.company_name} ${c.delivery_postcode || c.invoice_postcode || ''}`
    }))
  ];
}

const STATUS_WORDS = {
  held: 'Held — date to follow',
  planned: 'Redelivery planned',
  delivered: 'Redelivered',
  cancelled: 'Cancelled'
};

function RedeliverBody({ data, form, setForm, contacts, saving, cancelling, setCancelling, cancelReason, setCancelReason, error }) {
  useSlideOutButton([saving, cancelling, data]);
  const { order, lines, open, action, today, redeliveries } = data;
  const editing = !!open;
  const canWrite = !!action.mode;
  const earlier = (redeliveries || []).filter(r => !open || r.id !== open.id);
  const openLines = new Set((open?.lines || []).map(l => Number(l.order_line_id)));
  const dateProblem = redeliveryDateProblem({
    date: form.redeliveryDate, refusedOn: form.refusedOn, orderDate: order.delivery_date, today
  });

  return (
    <div className="redeliver-body">
      <p className="redeliver-intro">
        {order.order_number} · {order.company_name}{order.account_no ? ` (${order.account_no})` : ''} · delivery date {ukDay(order.delivery_date) || '—'}.
        {' '}The order and its PO-D stay exactly as they are: nothing is bought again, and the supplied quantity stays.
        {' '}On the redelivery date each ticked line goes on the planning board, collected from where it is held;
        {' '}its haulier, driver and vehicle are set there.
      </p>

      {!canWrite && action.block && (
        <p className="redeliver-block">
          <span className="material-symbols-rounded">info</span>
          {action.block}
        </p>
      )}

      <div className="redeliver-grid">
        <div className="redeliver-field redeliver-span">
          <label>Why was it refused? <span className="required-field">*</span></label>
          <Textarea
            value={form.reason}
            onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
            rows={3}
            maxLength={2000}
            disabled={!canWrite || saving}
            placeholder="e.g. no room in the shed — customer asked us to hold it"
          />
        </div>
        <div className="redeliver-field redeliver-span">
          <label>Where is it held? <span className="required-field">*</span></label>
          <SearchableDropdown
            value={form.heldAt}
            onChange={(v) => setForm(f => ({ ...f, heldAt: v || '' }))}
            options={heldAtOptions(contacts || [])}
            placeholder={contacts === null ? 'Loading contacts…' : 'Choose where it is held…'}
            placeholderClassName={form.heldAt ? '' : 'sd-placeholder-error'}
            searchPlaceholder="Search by # or name, or choose STOCK..."
            disabled={!canWrite || saving}
          />
          <span className="redeliver-hint">
            STOCK for our own site, or the contact storing it. The address is taken from the contact.
            {open?.held_address ? ` Now: ${open.held_address.split('\n').join(', ')}` : ''}
          </span>
        </div>
        <div className="redeliver-field">
          <label>Refused on <span className="required-field">*</span></label>
          <DateInput
            value={form.refusedOn}
            onChange={(e) => setForm(f => ({ ...f, refusedOn: e.target.value }))}
            min={order.delivery_date || undefined}
            max={today}
            disabled={!canWrite || saving}
          />
        </div>
        <div className="redeliver-field">
          <label>Redelivery date</label>
          <DateInput
            value={form.redeliveryDate}
            onChange={(e) => setForm(f => ({ ...f, redeliveryDate: e.target.value }))}
            min={today}
            disabled={!canWrite || saving}
            isClearable={canWrite && !saving}
          />
          <span className={`redeliver-hint${dateProblem ? ' is-error' : ''}`}>
            {dateProblem || (form.redeliveryDate
              ? `On the ${ukDay(form.redeliveryDate)} planning board — set its haulier, driver and vehicle there.`
              : 'Blank: held, date to follow.')}
          </span>
        </div>
      </div>

      <div className="redeliver-lines">
        <table className="redeliver-lines-table">
          <thead>
            <tr>
              <th aria-label="Redeliver" />
              <th>Product</th>
              <th className="num">Ordered</th>
              <th className="num">Supplied</th>
              <th className="num">Redelivered</th>
              <th>PO</th>
            </tr>
          </thead>
          <tbody>
            {lines.map(l => {
              const ticked = editing ? openLines.has(l.id) : form.lineIds.has(l.id);
              return (
                <tr key={l.id} className={ticked ? '' : 'is-off'}>
                  <td>
                    <TickCheckbox
                      id={`redeliver-line-${l.id}`}
                      checked={ticked}
                      disabled={editing || !canWrite || saving}
                      ariaLabel={`Redeliver ${l.product_name || l.product_code}`}
                      onChange={() => setForm(f => {
                        const next = new Set(f.lineIds);
                        if (next.has(l.id)) next.delete(l.id); else next.add(l.id);
                        return { ...f, lineIds: next };
                      })}
                    />
                  </td>
                  <td>{l.product_code ? <span className="redeliver-code">{l.product_code}</span> : null}{l.product_name}</td>
                  <td className="num">{l.ordered == null ? '—' : formatQuantity(l.ordered)}</td>
                  <td className="num">{l.supplied == null ? '—' : formatQuantity(l.supplied)}</td>
                  <td className="num" title="The whole line goes — part quantities are not in this version">{formatQuantity(l.quantity)}</td>
                  <td>{l.purchase_order_number || 'Stock'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && canWrite && (
        <div className={`redeliver-cancel${cancelling ? ' is-open' : ''}`}>
          {cancelling ? (
            <>
              <label>Why is the redelivery cancelled? <span className="required-field">*</span></label>
              <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} maxLength={2000}
                placeholder="e.g. customer collected it from the store" disabled={saving} />
              <span className="redeliver-hint">Its rows leave the planning board and the order reads Delivered again. Use Cancel redelivery below to confirm.</span>
            </>
          ) : (
            <button type="button" className="redeliver-link" data-write-action onClick={() => setCancelling(true)}>
              Cancel this redelivery…
            </button>
          )}
        </div>
      )}

      {error && <p className="redeliver-error">{error}</p>}

      {earlier.length > 0 && (
        <div className="redeliver-history">
          <h4>Earlier</h4>
          <ul>
            {earlier.map(r => (
              <li key={r.id}>
                <strong>{STATUS_WORDS[effectiveRedeliveryStatus(r, today)] || r.status}</strong>
                {' '}— refused {ukShortDay(r.refused_on)}, held at {heldAtLabel(r) || '—'}
                {r.redelivery_date ? `, redelivery ${ukShortDay(r.redelivery_date)}` : ''}.
                {' '}{r.reason}
                {r.cancel_reason ? ` (cancelled: ${r.cancel_reason})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="redeliver-scope">
        <span className="material-symbols-rounded">info</span>
        {REDELIVERY_OUT_OF_SCOPE}
      </p>
    </div>
  );
}

/**
 * @param {number}   orderId   The customer order (a PO's hub order).
 * @param {function} onClose   Always called when the window closes.
 * @param {function} onDone    (message) after a save or a cancel.
 */
function RedeliverOrderModal({ orderId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [form, setForm] = useState({ reason: '', heldAt: '', refusedOn: '', redeliveryDate: '', lineIds: new Set() });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/redelivery`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || `Could not load (${res.status})`);
      setData(body);
      const o = body.open;
      setForm(o
        ? {
          reason: o.reason || '',
          heldAt: o.held_at_stock ? REDELIVERY_STOCK_VALUE : (o.held_at_account_id ? String(o.held_at_account_id) : ''),
          refusedOn: o.refused_on || '',
          redeliveryDate: o.redelivery_date || '',
          lineIds: new Set((o.lines || []).map(l => Number(l.order_line_id)))
        }
        : {
          reason: '', heldAt: '', refusedOn: body.order?.delivery_date || '', redeliveryDate: '',
          lineIds: new Set((body.lines || []).map(l => l.id))
        });
    } catch (err) {
      setLoadError(err.message);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&limit=10000`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setContacts(d.contacts || []); })
      .catch(() => { if (!cancelled) setContacts([]); });
    return () => { cancelled = true; };
  }, []);

  const editing = !!data?.open;
  const canWrite = !!data?.action?.mode;
  const problem = useMemo(() => {
    if (!data) return 'Loading…';
    if (!canWrite) return data.action?.block || 'Not available';
    return redeliverySaveProblem({
      reason: form.reason,
      heldAtStock: form.heldAt === REDELIVERY_STOCK_VALUE,
      heldAtAccountId: form.heldAt === REDELIVERY_STOCK_VALUE ? null : form.heldAt,
      refusedOn: form.refusedOn,
      // An edit that leaves the date as it was is not checked again.
      redeliveryDate: editing && form.redeliveryDate === (data.open.redelivery_date || '') ? null : form.redeliveryDate,
      lineIds: [...form.lineIds],
      orderDate: data.order?.delivery_date,
      today: data.today,
      isNew: !editing
    });
  }, [data, form, canWrite, editing]);

  const send = async (url, method, body) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.success) throw new Error(out.error || `Could not save (${res.status})`);
      onDone && onDone(out.message);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const save = () => {
    if (problem) return;
    const base = `${API_URL}/api/orders/customer-orders/${orderId}/redelivery`;
    const body = {
      reason: form.reason.trim(),
      held_at: form.heldAt,
      refused_on: form.refusedOn,
      redelivery_date: form.redeliveryDate || null
    };
    if (editing) send(base, 'PUT', body);
    else send(base, 'POST', { ...body, order_line_ids: [...form.lineIds] });
  };

  const cancelRedelivery = () => {
    if (!cancelReason.trim()) { setError('Say why the redelivery is cancelled'); return; }
    send(`${API_URL}/api/orders/customer-orders/${orderId}/redelivery/cancel`, 'POST', { reason: cancelReason.trim() });
  };

  const title = data
    ? `${editing ? 'Redelivery' : 'Refused — redeliver'} — ${data.order.order_number}`
    : 'Refused — redeliver';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="lg"
      className="redeliver-order"
      footer={(
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Close</span>
            </button>
          </div>
          {editing && cancelling && (
            <div>
              <button data-write-action className="btn-slideout" onClick={cancelRedelivery}
                disabled={saving || !cancelReason.trim()}
                title={cancelReason.trim() ? 'Cancel the redelivery — its rows leave the board' : 'Say why it is cancelled first'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">event_busy</span></div>
                <span className="btn-label">{saving ? 'Cancelling…' : 'Cancel redelivery'}</span>
              </button>
            </div>
          )}
          <div>
            <button data-write-action className="btn-slideout" onClick={save} disabled={saving || !!problem}
              title={problem || (editing ? 'Save the changes to this redelivery' : 'Record the refused delivery')}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving…' : (editing ? 'Save changes' : 'Record refused delivery')}</span>
            </button>
          </div>
        </div>
      )}
    >
      {loadError ? (
        <p className="redeliver-error">{loadError}</p>
      ) : !data ? (
        <LoadingSpinner />
      ) : (
        <RedeliverBody
          data={data}
          form={form}
          setForm={setForm}
          contacts={contacts}
          saving={saving}
          cancelling={cancelling}
          setCancelling={setCancelling}
          cancelReason={cancelReason}
          setCancelReason={setCancelReason}
          error={error}
        />
      )}
    </Modal>
  );
}

export default RedeliverOrderModal;
